import uniq from 'lodash/uniq';
import * as React from 'react';
import { v4 as uuid } from 'uuid';

import { useQueryBuilderContext } from 'bones/components/molecules/QueryBuilder/hooks/useQueryBuilder';
import { useQueryBuilderModelContext } from 'bones/components/molecules/QueryBuilder/hooks/useQueryBuilderModel';
import { useQueryBuilderSchemaContext } from 'bones/components/molecules/QueryBuilder/hooks/useQueryBuilderSchema';
import { emptyArray } from 'bones/constants/empty';
import {
  RDF_TYPE_IRI,
  STARDOG_LABEL_IRI,
  XSD_STRING_IRI,
} from 'bones/constants/iris';
import { getLocalName } from 'bones/utils/getLocalName';
import { canCompareDatatypeRange } from 'bones/utils/queryBuilder/canCompareDatatypeRange';
import {
  ANY_CLASS_IRI,
  Direction,
  INSTANCE_IRI,
  OperatorType,
} from 'bones/utils/queryBuilder/constants';
import { getNumberedLabel } from 'bones/utils/queryBuilder/getNumberedLabel';
import {
  ClassSelectItem,
  PropertySelectItem,
  PropertySubSelectItem,
  createClassSelectItem,
  createPropertySubSelectItem,
} from 'bones/utils/queryBuilder/helpers/select';
import { QueryBuilderClass } from 'bones/utils/queryBuilder/model';
import { DraftFilter, Variable } from 'bones/utils/queryBuilder/types';
import { getClassifyClassesByIri } from 'bones/utils/queryBuilder/validation/getClassifyClassesByIri';
import { getInvalidInstanceSubjects } from 'bones/utils/queryBuilder/validation/getInvalidInstanceSubjects';
import { getValidVariables } from 'bones/utils/queryBuilder/validation/getValidVariables';
import { validateInstanceProperty } from 'bones/utils/queryBuilder/validation/validateInstanceProperty';
import { alphaSort } from 'bones/utils/sort/alphaSort';

const itemTextSort = alphaSort(({ text }: { text: string }) => text);

export const useAnyClassItem = () => {
  const { colors, copy } = useQueryBuilderSchemaContext();
  return React.useMemo(
    () =>
      createClassSelectItem({
        iri: ANY_CLASS_IRI,
        label: copy.body.class.anyLabel,
        colorId: colors.anyClassColorId,
        className: 'sd-heading',
      }),
    [colors, copy]
  );
};

export const useLabelPropertyItem = () => {
  const { copy } = useQueryBuilderSchemaContext();
  return React.useMemo(
    () =>
      createPropertySubSelectItem(
        {
          iri: STARDOG_LABEL_IRI,
          operatorType: OperatorType.DATATYPE,
          label: copy.body.property.label,
          ranges: new Set([XSD_STRING_IRI]),
        },
        Direction.OUTBOUND
      ),
    [copy]
  );
};

export const useModelClassItems = (): ClassSelectItem[] => {
  const { classesByIri } = useQueryBuilderModelContext();
  const anyClassItem = useAnyClassItem();

  const items = Object.values(classesByIri)
    .map<ClassSelectItem>(createClassSelectItem)
    .sort(itemTextSort);
  items.unshift(anyClassItem);

  return items;
};

export const useClassifyClassesByIri = () => {
  const { topExpression } = useQueryBuilderContext();

  return React.useMemo(
    () => getClassifyClassesByIri(topExpression),
    [topExpression]
  );
};

export const useGetClassItemsForPropertySelector = (): ((
  operator: DraftFilter['operator']
) => ClassSelectItem[]) => {
  const { propertiesByIri } = useQueryBuilderModelContext();
  const classItems = useModelClassItems();
  const classifyClassesByIri = useClassifyClassesByIri();

  return React.useCallback(
    (operator: DraftFilter['operator']) => {
      if (!operator || operator.operatorType !== OperatorType.OBJECT) {
        return emptyArray as any;
      }

      const classIris = new Set(
        operator.direction === Direction.OUTBOUND
          ? propertiesByIri[operator.iri]?.ranges
          : propertiesByIri[operator.iri]?.domains
      );

      Object.entries(classifyClassesByIri).forEach(
        ([classIri, classifyClasses]) => {
          Array.from(classifyClasses).forEach((classifyIri) => {
            if (classIris.has(classifyIri)) {
              classIris.add(classIri);
            }
          });
        }
      );

      return classIris.size
        ? classItems.filter(
            ({ value }) => value === ANY_CLASS_IRI || classIris.has(value)
          )
        : classItems;
    },
    [classifyClassesByIri, classItems, propertiesByIri]
  );
};

const sortClassItems = (a: ClassSelectItem, b: ClassSelectItem) => {
  // istanbul ignore if
  if (a.value === ANY_CLASS_IRI) {
    return -1;
  }
  // istanbul ignore if
  if (b.value === ANY_CLASS_IRI) {
    return 1;
  }
  return alphaSort<ClassSelectItem>(({ text }) => text)(a, b);
};

const getValidClassItems = (
  allItems: ClassSelectItem[],
  variables: Record<string, string[]>,
  getVariableLabel: (
    label: string,
    { id, iri }: Variable
  ) => { label: string; num: number | null }
) => {
  const items: ClassSelectItem[] = [];
  Object.entries(variables).forEach(([iri, validIds]) => {
    const item = allItems.find((i) => i.value === iri);
    if (!item) {
      return;
    }

    items.push({
      ...item,
      items: validIds
        .map((id) => {
          const { label, num } = getVariableLabel(item.text, { iri, id });
          return {
            text: getNumberedLabel(label, num),
            searchText: item.text,
            value: id,
            iri,
          };
        })
        .sort(itemTextSort),
    });
  });

  return items.sort(sortClassItems);
};

const filterVariables = (
  validVariables: Record<string, string[]>,
  invalidVariables: Record<string, string[]>
) =>
  Object.entries(validVariables).reduce<Record<string, string[]>>(
    (acc, [iri, ids]) => {
      const invalidIds = new Set(invalidVariables[iri]);
      // istanbul ignore else
      if (!invalidIds.size) {
        acc[iri] = ids;
      }
      const filtered = ids.filter((id) => !invalidIds.has(id));
      // istanbul ignore else
      if (filtered.length) {
        acc[iri] = filtered;
      }
      return acc;
    },
    {}
  );

export const useValidClassSubjectItems = ({
  subExpressionIdx,
  expressionIdx,
  filterIdx,
}: {
  subExpressionIdx: number;
  expressionIdx: number;
  filterIdx: number;
}): ClassSelectItem[] => {
  const { copy } = useQueryBuilderSchemaContext();
  const { topExpression, validVariables, getVariableLabel } =
    useQueryBuilderContext();
  const allClassItems = useModelClassItems();

  return React.useMemo(() => {
    const { subject } =
      topExpression.expressions[subExpressionIdx].expressions[expressionIdx]
        .filters[filterIdx];

    const instanceVariables = getInvalidInstanceSubjects({
      topExpression,
      subExpressionIdx,
      expressionIdx,
      filterIdx,
    });

    if (validVariables) {
      return getValidClassItems(
        allClassItems,
        filterVariables(validVariables, instanceVariables),
        getVariableLabel
      );
    }

    // hide items for other iris that have instances defined elsewhere
    const filteredClassItems = allClassItems.filter(
      (item) => item.value === subject?.iri || !instanceVariables[item.value]
    );

    const validSubjects = getValidVariables({
      topExpression,
      subExpressionIdx,
      expressionIdx,
      filterIdx,
    });

    return filteredClassItems.map((item) => {
      const iri = item.value;
      const validIds = validSubjects[item.value];

      if (instanceVariables[item.value] && subject) {
        const { label, num } = getVariableLabel(item.text, subject);
        const textLabel = getNumberedLabel(label, num);
        return {
          ...item,
          items: [
            {
              text:
                validIds.length === 1 && textLabel === item.text
                  ? copy.body.class.existingInstance
                  : textLabel,
              searchText: item.text,
              value: subject.id,
              iri,
            },
          ],
        };
      }

      const filteredSubjects = filterVariables(
        validSubjects,
        instanceVariables
      );
      return {
        ...item,
        items: [
          ...(filteredSubjects[item.value] || [])
            .map((id) => {
              const { label, num } = getVariableLabel(item.text, { iri, id });
              const textLabel = getNumberedLabel(label, num);
              return {
                text:
                  validIds.length === 1 && textLabel === item.text
                    ? copy.body.class.existingInstance
                    : textLabel,
                searchText: item.text,
                value: id,
                iri,
              };
            })
            .sort(itemTextSort),
          {
            text: copy.body.class.newInstance,
            searchText: item.text,
            value: uuid(),
            iri,
          },
        ],
      };
    });
  }, [
    allClassItems,
    copy,
    expressionIdx,
    filterIdx,
    getVariableLabel,
    subExpressionIdx,
    topExpression,
    validVariables,
  ]);
};

export const useGetComparisonSubjectItems = (): ((
  operator: DraftFilter['operator'],
  args: {
    subExpressionIdx: number;
    expressionIdx: number;
    filterIdx: number;
  }
) => ClassSelectItem[]) => {
  const { copy } = useQueryBuilderSchemaContext();
  const { topExpression, getVariableLabel } = useQueryBuilderContext();
  const { propertiesByIri } = useQueryBuilderModelContext();
  const allClassItems = useModelClassItems();
  const { datatypeProperties, unboundDatatypeProperties } =
    useModelPropertyItems();
  const labelProperty = useLabelPropertyItem();

  return React.useCallback(
    (
      operator: DraftFilter['operator'],
      {
        subExpressionIdx,
        expressionIdx,
        filterIdx,
      }: {
        subExpressionIdx: number;
        expressionIdx: number;
        filterIdx: number;
      }
    ) => {
      if (!operator || operator.operatorType !== OperatorType.DATATYPE) {
        return emptyArray as any;
      }

      const validValues = getValidVariables({
        topExpression,
        subExpressionIdx,
        expressionIdx,
        filterIdx,
      });

      let items = allClassItems;

      // no need to filter if there are unbound properties as unbound properties apply to all classes
      const operatorRange = operator.range || XSD_STRING_IRI;
      if (
        ![labelProperty, ...unboundDatatypeProperties].some(
          ({ range }) => operatorRange === (range || XSD_STRING_IRI)
        )
      ) {
        const validClassIris = new Set<string>();
        datatypeProperties.forEach(({ range, iri: propertyIri }) => {
          if (operatorRange !== (range || XSD_STRING_IRI)) {
            return;
          }
          propertiesByIri[propertyIri]?.domains.forEach((domain) =>
            validClassIris.add(domain)
          );
        });
        items = items.filter(
          (item) =>
            item.value === ANY_CLASS_IRI || validClassIris.has(item.value)
        );
      }

      return items.map((item) => {
        const iri = item.value;
        const validIds = validValues[iri] || [];
        return {
          ...item,
          items: [
            ...validIds
              .map((id) => {
                const { label, num } = getVariableLabel(item.text, { iri, id });
                const textLabel = getNumberedLabel(label, num);
                return {
                  text:
                    validIds.length === 1 && textLabel === item.text
                      ? copy.body.class.existingInstance
                      : textLabel,
                  searchText: item.text,
                  value: id,
                  iri,
                };
              })
              .sort(itemTextSort),
            {
              text: copy.body.class.newInstance,
              searchText: item.text,
              value: uuid(),
              iri,
            },
          ],
        };
      });
    },
    [
      allClassItems,
      copy,
      datatypeProperties,
      getVariableLabel,
      labelProperty,
      propertiesByIri,
      topExpression,
      unboundDatatypeProperties,
    ]
  );
};

export const useGetClassValueItems = (): ((
  operator: DraftFilter['operator'],
  args: {
    subExpressionIdx: number;
    expressionIdx: number;
    filterIdx: number;
  }
) => ClassSelectItem[]) => {
  const { copy } = useQueryBuilderSchemaContext();
  const { topExpression, validVariables, getVariableLabel } =
    useQueryBuilderContext();
  const { classesByIri } = useQueryBuilderModelContext();
  const getAllClassItems = useGetClassItemsForPropertySelector();

  return React.useCallback(
    (
      operator: DraftFilter['operator'],
      {
        subExpressionIdx,
        expressionIdx,
        filterIdx,
      }: {
        subExpressionIdx: number;
        expressionIdx: number;
        filterIdx: number;
      }
    ) => {
      const allClassItems = getAllClassItems(operator);

      if (operator?.iri === RDF_TYPE_IRI) {
        const subjectIri =
          topExpression.expressions[subExpressionIdx].expressions[expressionIdx]
            .filters[filterIdx].subject?.iri;
        const modelClass: QueryBuilderClass | undefined =
          classesByIri[subjectIri || /* istanbul ignore next */ ''];

        const modelClassItems = allClassItems
          .filter(
            (item) =>
              item.value !== ANY_CLASS_IRI &&
              item.value !== subjectIri &&
              !modelClass?.ancestors.has(item.value)
          )
          .map((item) => ({
            ...item,
            items: [
              {
                text: item.text,
                iri: item.value,
                value: uuid(),
              },
            ],
          }));

        const newClassItems = topExpression.expressions.reduce<
          Record<string, ClassSelectItem>
        >((acc, { expressions }) => {
          expressions.forEach(({ filters }) => {
            filters.forEach((filter) => {
              const newSubjectIri = filter.subject?.iri;
              if (
                !newSubjectIri ||
                !(
                  newSubjectIri === subjectIri ||
                  modelClass?.descendants.has(newSubjectIri)
                )
              ) {
                return;
              }

              if (
                typeof filter.value !== 'string' &&
                filter.value?.isNewlyCreated &&
                !acc[filter.value.iri]
              ) {
                const newIri = filter.value.iri;
                const newLabel = filter.value.newLabel || getLocalName(newIri);
                acc[newIri] = {
                  ...createClassSelectItem({
                    iri: newIri,
                    label: newLabel,
                    colorId: 'sd-qb-black',
                  }),
                  isNewlyCreated: true,
                  items: [
                    {
                      text: newLabel,
                      value: uuid(),
                      iri: newIri,
                      isNewlyCreated: true,
                    },
                  ],
                };
              }
            });
          });

          return acc;
        }, {});

        return [...modelClassItems, ...Object.values(newClassItems)];
      }

      if (validVariables) {
        return getValidClassItems(
          allClassItems,
          validVariables,
          getVariableLabel
        );
      }

      const validValues = getValidVariables({
        topExpression,
        subExpressionIdx,
        expressionIdx,
        filterIdx,
      });

      return allClassItems.map((item) => {
        const iri = item.value;
        const validIds = validValues[iri] || [];
        return {
          ...item,
          items: [
            ...validIds
              .map((id) => {
                const { label, num } = getVariableLabel(item.text, { iri, id });
                const textLabel = getNumberedLabel(label, num);
                return {
                  text:
                    validIds.length === 1 && textLabel === item.text
                      ? copy.body.class.existingInstance
                      : textLabel,
                  searchText: item.text,
                  value: id,
                  iri,
                };
              })
              .sort(itemTextSort),
            {
              text: copy.body.class.newInstance,
              searchText: item.text,
              value: uuid(),
              iri,
            },
          ],
        };
      });
    },
    [
      classesByIri,
      copy,
      getAllClassItems,
      getVariableLabel,
      topExpression,
      validVariables,
    ]
  );
};

export const useInstancePropertyItem = () => {
  const { copy } = useQueryBuilderSchemaContext();
  return React.useMemo(
    () =>
      createPropertySubSelectItem(
        {
          iri: INSTANCE_IRI,
          label: copy.body.property.instance,
          operatorType: OperatorType.INSTANCE,
          ranges: new Set(),
        },
        Direction.OUTBOUND
      ),
    [copy]
  );
};

export const useModelPropertyItems = () => {
  const { propertiesByIri } = useQueryBuilderModelContext();
  const instanceProperty = useInstancePropertyItem();

  return React.useMemo(() => {
    const datatypeProperties: PropertySubSelectItem[] = [];
    const objectProperties: PropertySubSelectItem[] = [];
    const unboundDatatypeProperties: PropertySubSelectItem[] = [];
    const unboundObjectProperties: PropertySubSelectItem[] = [];
    const typeProperties: PropertySubSelectItem[] = [];

    Object.values(propertiesByIri).forEach(
      ({ iri, label, isDatatype, domains, ranges }) => {
        const outbound = createPropertySubSelectItem(
          {
            iri,
            label,
            operatorType: isDatatype
              ? OperatorType.DATATYPE
              : OperatorType.OBJECT,
            ranges,
          },
          Direction.OUTBOUND
        );
        const inbound = createPropertySubSelectItem(
          {
            iri,
            label,
            operatorType: isDatatype
              ? OperatorType.DATATYPE
              : OperatorType.OBJECT,
            ranges,
          },
          Direction.INBOUND
        );

        if (iri === RDF_TYPE_IRI) {
          typeProperties.push(outbound);
          return;
        }

        const arr = isDatatype ? datatypeProperties : objectProperties;
        arr.push(outbound);
        // do not add datatype ranges
        if (!isDatatype) {
          arr.push(inbound);
        }

        const unboundArr = isDatatype
          ? unboundDatatypeProperties
          : unboundObjectProperties;
        if (!domains.size) {
          unboundArr.push(outbound);
        }
        if (!isDatatype && !ranges.size) {
          unboundArr.push(inbound);
        }
      }
    );

    return {
      allProperties: [
        instanceProperty,
        ...objectProperties,
        ...datatypeProperties,
        ...unboundDatatypeProperties,
        ...unboundObjectProperties,
      ],
      datatypeProperties,
      objectProperties,
      unboundDatatypeProperties,
      unboundObjectProperties,
      typeProperties,
    };
  }, [instanceProperty, propertiesByIri]);
};

export const useGetPropertyItemsForClass = (): ((
  classIri: string,
  args: {
    subExpressionIdx: number;
    expressionIdx: number;
    filterIdx: number;
  }
) => PropertySelectItem[]) => {
  const { topExpression } = useQueryBuilderContext();
  const { copy, instanceDisabled } = useQueryBuilderSchemaContext();
  const { classesByIri } = useQueryBuilderModelContext();
  const allItems = useModelPropertyItems();
  const instanceProperty = useInstancePropertyItem();
  const labelProperty = useLabelPropertyItem();

  const classifyClassesByIri = React.useMemo(
    () => getClassifyClassesByIri(topExpression),
    [topExpression]
  );

  return React.useCallback(
    (
      classIri: string,
      {
        subExpressionIdx,
        expressionIdx,
        filterIdx,
      }: {
        subExpressionIdx: number;
        expressionIdx: number;
        filterIdx: number;
      }
    ) => {
      const isInstanceValid =
        !instanceDisabled &&
        validateInstanceProperty({
          topExpression,
          subExpressionIdx,
          expressionIdx,
          filterIdx,
        });

      const incomingIris = new Set(classesByIri[classIri]?.incomingProperties);
      const outgoingIris = new Set(classesByIri[classIri]?.outgoingProperties);
      const classifyClasses = classifyClassesByIri[classIri];
      if (classifyClasses) {
        Array.from(classifyClasses).forEach((classifyIri) => {
          classesByIri[classifyIri]?.incomingProperties.forEach((property) => {
            incomingIris.add(property);
          });
          classesByIri[classifyIri]?.outgoingProperties.forEach((property) => {
            outgoingIris.add(property);
          });
        });
      }

      const itemFilter =
        classIri === ANY_CLASS_IRI
          ? () => true
          : ({ iri, direction }: PropertySubSelectItem) =>
              direction === Direction.INBOUND
                ? incomingIris.has(iri)
                : outgoingIris.has(iri);

      const datatypeProperties = uniq(
        allItems.datatypeProperties
          .filter(itemFilter)
          .concat(allItems.unboundDatatypeProperties)
      ).sort(itemTextSort);

      const objectProperties = uniq(
        allItems.objectProperties
          .filter(itemFilter)
          .concat(allItems.unboundObjectProperties)
      ).sort(itemTextSort);

      return [
        ...(isInstanceValid ? [instanceProperty] : []),
        ...(objectProperties.length
          ? [
              {
                text: copy.body.property.relationships,
                value: copy.body.property.relationships,
                items: objectProperties,
              },
            ]
          : []),
        ...(datatypeProperties.length
          ? [
              {
                text: copy.body.property.attributes,
                value: copy.body.property.attributes,
                items: datatypeProperties,
              },
            ]
          : []),
        labelProperty,
        ...allItems.typeProperties,
      ];
    },
    [
      allItems,
      classesByIri,
      classifyClassesByIri,
      copy,
      instanceDisabled,
      instanceProperty,
      labelProperty,
      topExpression,
    ]
  );
};

export const useGetComparisonPropertyItems = (): ((
  classIri: string,
  operator: DraftFilter['operator']
) => PropertySelectItem[]) => {
  const { copy } = useQueryBuilderSchemaContext();
  const { classesByIri } = useQueryBuilderModelContext();
  const allItems = useModelPropertyItems();
  const labelProperty = useLabelPropertyItem();

  return React.useCallback(
    (classIri: string, operator: DraftFilter['operator']) => {
      if (!operator || operator.operatorType !== OperatorType.DATATYPE) {
        return emptyArray as any;
      }

      const outgoingIris = new Set(classesByIri[classIri]?.outgoingProperties);

      const itemFilter =
        classIri === ANY_CLASS_IRI
          ? () => true
          : ({ iri }: PropertySubSelectItem) => outgoingIris.has(iri);

      const datatypeProperties = uniq(
        allItems.datatypeProperties
          .filter(itemFilter)
          .concat(allItems.unboundDatatypeProperties)
          .filter(({ range }) =>
            canCompareDatatypeRange(operator.range || null, range || null)
          )
      ).sort(itemTextSort);

      const items: PropertySelectItem[] = [];

      if (datatypeProperties.length) {
        items.push({
          text: copy.body.property.attributes,
          value: copy.body.property.attributes,
          items: datatypeProperties,
        });
      }

      if ((operator.range || XSD_STRING_IRI) === XSD_STRING_IRI) {
        items.push(labelProperty);
      }

      return items;
    },
    [allItems, classesByIri, copy, labelProperty]
  );
};
